import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import { Card, Button, Badge, Spinner } from '../components/ui';
import { 
  Crown, 
  Check, 
  Sparkles,
  Shield,
  Zap,
  Star,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EUR_LANGS = ['de', 'fr', 'it', 'es', 'pt'];

const Pricing = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [packages, setPackages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [error, setError] = useState(null);

  const currency = EUR_LANGS.includes(i18n.language) ? 'EUR' : 'USD';
  const currencySymbol = currency === 'EUR' ? '€' : '$';

  useEffect(() => {
    fetchPackages();
  }, [i18n.language]);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/packages?lang=${i18n.language}`);
      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      }
    } catch (error) {
      // Silent fail - use hardcoded defaults
    }
  };

  const handleSubscribe = async (packageId) => {
    setLoading(true);
    setSelectedPackage(packageId);
    
    try {
      const response = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
          lang: i18n.language
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Iyzico Checkout
        window.location.href = data.url;
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Odeme baslatilamadi');
      }
    } catch (err) {
      setError('Odeme baslatilirken bir hata olustu. Lutfen tekrar deneyin.');
    } finally {
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  const monthlyPrice = packages?.monthly?.price || (currency === 'EUR' ? '5.99' : '4.99');
  const yearlyPrice = packages?.yearly?.price || (currency === 'EUR' ? '44.99' : '39.99');

  const plans = [
    {
      id: 'free',
      name: t('pricing.free', 'Free'),
      price: '0',
      period: '',
      description: t('pricing.freeDesc', 'Perfect to get started'),
      features: [
        t('pricing.feat3tattoos', '3 tattoo records'),
        t('pricing.featBasicReminders', 'Basic care reminders'),
        t('pricing.featCareGuide', 'Care guide'),
        t('pricing.featArtistSearch', 'Artist search')
      ],
      limitations: [
        t('pricing.limNoAI', 'No AI color analysis'),
        t('pricing.limLimitedMap', 'Limited body map'),
        t('pricing.limAds', 'Ad supported')
      ],
      cta: t('pricing.currentPlan', 'Current Plan'),
      popular: false,
      disabled: true
    },
    {
      id: 'monthly',
      name: t('pricing.monthlyName', 'Premium Monthly'),
      price: monthlyPrice,
      period: '/' + t('pricing.month', 'mo'),
      description: t('pricing.monthlyDesc', 'Most popular choice'),
      features: [
        t('pricing.featUnlimited', 'Unlimited tattoo records'),
        t('pricing.featAIFade', 'AI fade analysis'),
        t('pricing.featFullMap', 'Full body map'),
        t('pricing.featAdvReminders', 'Advanced reminders'),
        t('pricing.featReviews', 'Artist reviews'),
        t('pricing.featNoAds', 'Ad-free experience'),
        t('pricing.featSupport', 'Priority support')
      ],
      limitations: [],
      cta: t('pricing.subscribe', 'Subscribe'),
      popular: true,
      disabled: false
    },
    {
      id: 'yearly',
      name: t('pricing.yearlyName', 'Premium Yearly'),
      price: yearlyPrice,
      period: '/' + t('pricing.year', 'yr'),
      description: t('pricing.yearlyDesc', '2 months free!'),
      features: [
        t('pricing.featAllPremium', 'All Premium features'),
        t('pricing.featAR', 'AR tattoo preview'),
        t('pricing.featBadge', 'Special badge'),
        t('pricing.featBeta', 'Early access to beta features'),
        t('pricing.featVIP', '24/7 VIP support'),
        t('pricing.featFamily', 'Family sharing (coming soon)')
      ],
      limitations: [],
      cta: t('pricing.bestPrice', 'Best Price'),
      popular: false,
      savings: t('pricing.savings', 'Save $20'),
      disabled: false
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Badge className="mb-4">
            <Sparkles className="w-3 h-3 mr-1" />
            {t('pricing.premiumPlans', 'Premium Plans')}
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl mb-4" data-testid="pricing-title">
            {t('pricing.heroTitle', 'POWER UP YOUR')} <span className="text-primary">{t('pricing.heroHighlight', 'TATTOO JOURNEY')}</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('pricing.heroDesc', 'Access unlimited tattoo records, AI-powered analysis and more with Premium.')}
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card 
                className={`p-6 h-full flex flex-col relative ${
                  plan.popular ? 'border-primary ring-2 ring-primary/20' : ''
                }`}
                data-testid={`pricing-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge>{t('pricing.popular', 'Most Popular')}</Badge>
                  </div>
                )}
                
                {plan.savings && (
                  <div className="absolute -top-3 right-4">
                    <Badge variant="success">{plan.savings}</Badge>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="font-heading text-2xl mb-1">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                </div>

                <div className="mb-6">
                  <span className="text-4xl font-heading text-primary">{currencySymbol}{plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>

                <ul className="space-y-3 mb-6 flex-1">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {plan.limitations.map((limitation, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="w-4 h-4 flex items-center justify-center">-</span>
                      <span className="line-through">{limitation}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant={plan.popular ? 'default' : 'outline'}
                  className="w-full"
                  disabled={plan.disabled || loading}
                  onClick={() => !plan.disabled && handleSubscribe(plan.id)}
                  data-testid={`subscribe-${plan.id}`}
                >
                  {loading && selectedPackage === plan.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      {plan.id !== 'free' && <Crown className="w-4 h-4 mr-2" />}
                      {plan.cta}
                    </>
                  )}
                </Button>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-destructive/10 border border-destructive/30 rounded-sm text-center">
            <p className="text-destructive text-sm">{error}</p>
            <button onClick={() => setError(null)} className="text-xs text-muted-foreground mt-2 hover:underline">
              {t('common.close', 'Close')}
            </button>
          </div>
        )}

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16"
        >
          <h2 className="font-heading text-2xl text-center mb-8">
            {t('pricing.whyPremium', 'Why')} <span className="text-primary">Premium?</span>
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Zap, title: t('pricing.featAITitle', 'AI Analysis'), desc: t('pricing.featAIDesc', 'Analyze color fading with AI') },
              { icon: Shield, title: t('pricing.featUnlimitedTitle', 'Unlimited Records'), desc: t('pricing.featUnlimitedDesc', 'Record all your tattoos') },
              { icon: Star, title: t('pricing.featSupportTitle', 'Priority Support'), desc: t('pricing.featSupportDesc', 'Response guaranteed within 24h') },
              { icon: Crown, title: t('pricing.featSpecialTitle', 'Special Features'), desc: t('pricing.featSpecialDesc', 'AR preview and more') },
            ].map((feature, i) => {
              const Icon = feature.icon;
              return (
                <Card key={i} className="p-4 text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </Card>
              );
            })}
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16 max-w-3xl mx-auto"
        >
          <h2 className="font-heading text-2xl text-center mb-8">
            {t('pricing.faqTitle', 'Frequently Asked')} <span className="text-primary">{t('pricing.faqHighlight', 'Questions')}</span>
          </h2>

          <div className="space-y-4">
            {[
              { q: t('pricing.faq1q', 'Can I cancel anytime?'), a: t('pricing.faq1a', 'Yes, you can cancel your subscription anytime. Your premium features will continue until the end of the current period.') },
              { q: t('pricing.faq2q', 'Is the payment secure?'), a: t('pricing.faq2a', 'Yes, all payments are securely processed via Iyzico. Your card details are never stored on our servers.') },
              { q: t('pricing.faq3q', 'Is there a free trial?'), a: t('pricing.faq3a', 'You can try basic features with the free plan. To try premium features, start with the monthly plan.') },
            ].map((faq, i) => (
              <Card key={i} className="p-4">
                <h3 className="font-medium mb-2">{faq.q}</h3>
                <p className="text-sm text-muted-foreground">{faq.a}</p>
              </Card>
            ))}
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Pricing;
